import { USER_WORKER_URL } from '../../../js/config.js';
import { interpolate } from '../../../js/translations.js';
import { tokenManager } from '../../../js/utils/TokenManager.js';
import { storageManager } from '../../../core/storage/index.js';
import { storage } from '../../../js/utils/storage.js';

const HANGOUT_DISCORD_INVITE_URL = 'https://discord.gg/S2Ez7UEvkc';
const HANGOUT_TIPS_COMPLETE_FLAG_PREFIX = 'hangout_tips_all_seen';
const HANGOUT_TIP_NOTIFICATION_KEYS = {
    1: 'tip_message_1',
    2: 'tip_message_2',
    3: 'tip_message_3',
    4: 'tip_message_4'
};

const HANGOUT_TIP_FALLBACK_MESSAGES = {
    tip_message_1: 'If you want to see all images available for this outfit, click on the button above and then on the character name.',
    tip_message_2: 'You can ask {characterName} to change location or wear something else (many outfits require at least a trial membership).',
    tip_message_3: 'Would you like to see more images for {characterName}? You can now create your own!',
    tip_message_4: 'If you ever meet an issue (or just want to say hi), join us on Discord! <a href="{discordUrl}" target="_blank" rel="noopener noreferrer" class="unified-notification-discord-link">Join Discord</a>'
};

const resolveHangoutTipCharacterName = async (hangoutState) => {
    const selectedCharacter = hangoutState?.getSelectedCharacter?.();
    if (selectedCharacter?.name) return selectedCharacter.name;

    const config = await storageManager.getItem('currentHangoutConfig');
    const firstCharacterName = config?.allCharacters?.[0]?.name;
    if (typeof firstCharacterName === 'string' && firstCharacterName.trim()) return firstCharacterName;

    return 'your character';
};

const resolveHangoutTipMessage = ({ tipIndex, lang, characterName }) => {
    const tipKey = HANGOUT_TIP_NOTIFICATION_KEYS[tipIndex];
    if (!tipKey) return null;

    const notificationsByLanguage = window.translations?.[lang]?.hangout?.notifications;
    const notificationsInEnglish = window.translations?.en?.hangout?.notifications;
    const template = notificationsByLanguage?.[tipKey]
        || notificationsInEnglish?.[tipKey]
        || HANGOUT_TIP_FALLBACK_MESSAGES[tipKey];

    if (typeof template !== 'string' || !template.trim()) return null;

    return interpolate(template, {
        characterName,
        discordUrl: HANGOUT_DISCORD_INVITE_URL
    });
};

const getHangoutTipsCompleteStorageKey = () => {
    const userId = storage.get('userData')?.id;
    return userId
        ? `${HANGOUT_TIPS_COMPLETE_FLAG_PREFIX}:${userId}`
        : HANGOUT_TIPS_COMPLETE_FLAG_PREFIX;
};

export async function maybeShowHangoutTipToast(hangoutState) {
    if (!tokenManager.isAuthenticated()) return;

    try {
        const tipsCompleteStorageKey = getHangoutTipsCompleteStorageKey();
        if (storage.get(tipsCompleteStorageKey)) return;

        const token = await tokenManager.getValidAccessToken?.();
        if (!token) return;

        const response = await fetch(`${USER_WORKER_URL}/increment-hangout-tip-progress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({})
        });

        if (!response.ok) {
            console.warn('[HangoutTips] Failed to update tip progression:', response.status);
            return;
        }

        const payload = await response.json();
        if (!payload?.success) return;
        if (payload?.all_tips_seen) {
            storage.set(tipsCompleteStorageKey, true);
        }
        if (!payload?.should_show) return;

        const tipIndex = Number(payload.tip_index || 0);
        if (!Number.isFinite(tipIndex) || tipIndex < 1) return;

        const langResolver = /** @type {any} */ (window)['getCurrentLanguage'];
        const lang = typeof langResolver === 'function' ? langResolver() : 'en';
        const characterName = await resolveHangoutTipCharacterName(hangoutState);
        const tipMessage = resolveHangoutTipMessage({ tipIndex, lang, characterName });
        if (!tipMessage) return;

        const notificationSystem = (await import('../../utils/UnifiedNotificationSystem.js')).default;
        notificationSystem.info(tipMessage, {
            duration: 0,
            closable: true,
            position: 'top-right'
        });

        try {
            const { getAnalyticsTracker } = await import('../../analytics/hangout-obs-tracker.js');
            getAnalyticsTracker().trackFeature('hangout_tip_seen');
        } catch (analyticsError) {
            console.warn('[HangoutTips] Failed to track tip toast analytics:', analyticsError);
        }
    } catch (error) {
        console.warn('[HangoutTips] Failed to resolve and show tip toast:', error);
    }
}
